import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Terminal } from "lucide-react";

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    email: params.get("email") || "",
    full_name: params.get("name") || "",
    pin: "", pinConfirm: "",
    income_goal: 120000, avg_commission_per_sale: 500, cost_per_lead: 10,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.pin.length !== 4 || !/^\d+$/.test(form.pin)) return toast.error("PIN must be 4 digits");
    if (form.pin !== form.pinConfirm) return toast.error("PIN does not match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", {
        email: form.email, full_name: form.full_name, pin: form.pin,
        income_goal: Number(form.income_goal), avg_commission_per_sale: Number(form.avg_commission_per_sale),
        cost_per_lead: Number(form.cost_per_lead),
      });
      if (data.email_sent) {
        toast.success("Verification code sent to your email");
      } else if (data.verification_code) {
        toast.info(`Dev code: ${data.verification_code}`);
      }
      navigate("/verify", { state: { email: data.email, code: data.verification_code, emailSent: data.email_sent } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex text-fg">
      <div className="w-full md:w-1/2 flex flex-col justify-center px-8 md:px-20 py-12" data-testid="signup-page">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <Terminal size={18} className="text-amber"/>
          <div className="font-display text-xl text-amber">COYOTE/LIFE</div>
        </Link>
        <div className="label-xs text-amber">&gt; STEP 01 / 02</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none mt-2">Onboard.</h1>
        <p className="mt-3 text-fg-muted font-mono text-sm">Agent profile · income goal · 4-digit PIN.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="label-xs mb-1 block">Full Name</label>
            <input className="input-term" data-testid="signup-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required placeholder="Jane Doe" />
          </div>
          <div>
            <label className="label-xs mb-1 block">Email</label>
            <input type="email" className="input-term" data-testid="signup-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="agent@agency.com" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-xs mb-1 block">4-Digit PIN</label>
              <input type="password" maxLength={4} inputMode="numeric" className="input-term tracking-[0.5em]" data-testid="signup-pin-input" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} required placeholder="••••" />
            </div>
            <div>
              <label className="label-xs mb-1 block">Confirm PIN</label>
              <input type="password" maxLength={4} inputMode="numeric" className="input-term tracking-[0.5em]" data-testid="signup-pin-confirm" value={form.pinConfirm} onChange={(e) => setForm({ ...form, pinConfirm: e.target.value.replace(/\D/g, "") })} required placeholder="••••" />
            </div>
          </div>

          <div className="border-t border-term pt-5 mt-5">
            <div className="label-xs text-amber mb-3">&gt; YOUR NUMBERS</div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label-xs mb-1 block">Goal $/yr</label>
                <input type="number" className="input-term" data-testid="signup-goal-input" value={form.income_goal} onChange={(e) => setForm({ ...form, income_goal: e.target.value })} />
              </div>
              <div>
                <label className="label-xs mb-1 block">Comm/Sale</label>
                <input type="number" className="input-term" data-testid="signup-commission-input" value={form.avg_commission_per_sale} onChange={(e) => setForm({ ...form, avg_commission_per_sale: e.target.value })} />
              </div>
              <div>
                <label className="label-xs mb-1 block">Cost/Lead</label>
                <input type="number" className="input-term" data-testid="signup-cpl-input" value={form.cost_per_lead} onChange={(e) => setForm({ ...form, cost_per_lead: e.target.value })} />
              </div>
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="signup-submit-btn" className="btn-primary w-full mt-6">
            {loading ? "Executing..." : <>Create Account <ArrowRight size={14} /></>}
          </button>
          <div className="text-xs text-center text-fg-muted font-mono">
            Already have an account? <Link to="/login" className="text-amber" data-testid="signup-login-link">Sign in &gt;</Link>
          </div>
        </form>
      </div>

      <div className="hidden md:flex w-1/2 grid-bg border-l border-term items-center justify-center p-12 bg-raised">
        <div className="max-w-md">
          <div className="label-xs mb-4 text-amber">&gt; WHAT YOU GET</div>
          <ul className="space-y-5 text-base font-mono">
            <li className="flex gap-4"><span className="font-display text-amber w-10">01</span> Pre-licensing → state exam → E&O → first sale roadmap</li>
            <li className="flex gap-4"><span className="font-display text-amber w-10">02</span> Daily war room with live dial energy</li>
            <li className="flex gap-4"><span className="font-display text-amber w-10">03</span> AI coach auto-scoring every call</li>
            <li className="flex gap-4"><span className="font-display text-amber w-10">04</span> KPIs tied to your income goal</li>
            <li className="flex gap-4"><span className="font-display text-amber w-10">05</span> Leaderboard · competition · commission</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
